/**
 * Four-state discipline for the List & Grid Standard: Loading -> Error -> Empty ->
 * Loaded(+Pager) -> Refetching. Never render a half-state. First load = skeleton;
 * subsequent background refreshes = overlay over the stale list left mounted
 * (preserves data + scroll position). See `.github/context/list-grid-standard-behaviours.context.md` §13.
 */
import type { FC, ReactNode } from 'react';
import {
  Body1,
  Button,
  Skeleton,
  SkeletonItem,
  Spinner,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Box24Regular, ErrorCircle24Regular } from '@fluentui/react-icons';

const useSkeletonStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
  },
  row: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
  },
  cell: {
    flex: 1,
  },
});

export interface LoadingSkeletonProps {
  rows?: number;
  columns?: number;
}

/** First-load state — a skeleton grid, never a bare `<Spinner>`. */
export const LoadingSkeleton: FC<LoadingSkeletonProps> = ({ rows = 6, columns = 4 }) => {
  const styles = useSkeletonStyles();
  return (
    <Skeleton className={styles.root} aria-label="Loading">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className={styles.row}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <SkeletonItem key={colIndex} className={styles.cell} />
          ))}
        </div>
      ))}
    </Skeleton>
  );
};

const useMessageStateStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXXL,
  },
  icon: {
    fontSize: '2.5rem',
    color: tokens.colorNeutralForeground3,
  },
  errorIcon: {
    fontSize: '2.5rem',
    color: tokens.colorPaletteRedForeground1,
  },
  description: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '28rem',
  },
});

export interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
}

/** Error state — icon + title + message + "Try again". */
export const ErrorState: FC<ErrorStateProps> = ({
  message = 'Something went wrong while loading this list.',
  onRetry,
}) => {
  const styles = useMessageStateStyles();
  return (
    <div className={styles.root} role="alert">
      <ErrorCircle24Regular className={styles.errorIcon} />
      <Title3>Unable to Load Data</Title3>
      <Body1 className={styles.description}>{message}</Body1>
      {onRetry && (
        <Button appearance="secondary" onClick={onRetry}>
          Try Again
        </Button>
      )}
    </div>
  );
};

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
}

/** Empty state — icon + title + description + optional CTA. */
export const EmptyState: FC<EmptyStateProps> = ({
  icon = <Box24Regular />,
  title,
  description,
  actionLabel,
  onAction,
}) => {
  const styles = useMessageStateStyles();
  return (
    <div className={styles.root}>
      <span className={styles.icon}>{icon}</span>
      <Title3>{title}</Title3>
      {description && <Body1 className={styles.description}>{description}</Body1>}
      {actionLabel && onAction && (
        <Button appearance="primary" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

const useOverlayStyles = makeStyles({
  wrapper: {
    position: 'relative',
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    opacity: 0.6,
    zIndex: 1,
  },
});

export interface ListLoadingOverlayProps {
  /** `isFetching && !isLoading` — the inline refetch indicator, not the first-load spinner. */
  busy: boolean;
  children: ReactNode;
}

/** Wraps the stale list (left mounted) with a wash + spinner during a background refetch. */
export const ListLoadingOverlay: FC<ListLoadingOverlayProps> = ({ busy, children }) => {
  const styles = useOverlayStyles();
  return (
    <div className={styles.wrapper}>
      {children}
      {busy && (
        <div className={styles.overlay} data-testid="list-refetch-overlay">
          <Spinner size="medium" />
        </div>
      )}
    </div>
  );
};
